import React, { useEffect, useState, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MaterialSpinner from '../MaterialSpinner/MaterialSpinner';
import CoachingKangaroo from '../../../api/CoachingKangaroo';
import MaterialTableRow from '../MaterialTableRow/MaterialTableRow';
import * as conversationService from '../../../services/conversationService';
import {
  formatDateToYyyyMmDd,
  TIME_FROM,
  TIME_TO
} from '../../../utils/formattingUtils';
import { useTranslation } from 'react-i18next';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';

const columns = [
  { id: 'started-at', label: 'Date', src: "./ai_avatar.png" },
  { id: 'text', label: 'Conversation', maxWidth: 260 },
  { id: 'summaryAI', label: 'AI Summary' },
  { id: 'answer-count', label: 'Answers' },
  { id: 'first-score-average', label: 'Score (avg)' },
  { id: 'feeling', label: 'Feeling' },
  { id: 'status', label: 'Status' }
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 200px)'
  },
  container: {
    flex: '1 1 auto',
    overflowY: 'auto',
    overflowX: 'auto',
    minHeight: 0
  },
  selectBorderless: {
    '& .MuiInput-underline:before': { borderBottom: 'none' },
    '& .MuiInput-underline:after': { borderBottom: 'none' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' }
  },
  paginationBar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  },
  paginationInfo: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  paginationActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      justifyContent: 'flex-start'
    }
  }
}));
const buildJSONFilter = (filter, options = {}) => {
    const { limit, offset, total, lastSessionId, lastCreatedAt, firstSessionId, firstCreatedAt } = options;
    let dto = {}
    if (filter.dateFrom) {
        dto.at_from = formatDateToYyyyMmDd(filter.dateFrom) + TIME_FROM;
    }
    else {
        let date = new Date()
        date.setDate(date.getDate() - 1)
        dto.at_from = formatDateToYyyyMmDd(date) + TIME_FROM;
    }
    if (filter.dateTo) {
        dto.at_to = formatDateToYyyyMmDd(filter.dateTo) + TIME_TO;
    }
    else{
        dto.at_to = formatDateToYyyyMmDd(new Date()) + TIME_TO;
    }
    if (filter.selectedIntents && filter.selectedIntents.length > 0) {
        dto.intents = filter.selectedIntents;
    }
    if (filter.selectedEntities && filter.selectedEntities.length > 0) {
        dto.entities = filter.selectedEntities;
    }
    if (filter.selectedEntityValues && filter.selectedEntityValues.length > 0) {
        dto.entityValues = filter.selectedEntityValues;
    }
    if (filter.selectedStatuses && filter.selectedStatuses.length > 0) {
        dto.statuses = filter.selectedStatuses;
    }
    if (filter.selectedFeelings && filter.selectedFeelings.length > 0) {
        dto.feelings = filter.selectedFeelings;
    }
    if (filter.selectedRatings && filter.selectedRatings.length > 0) {
        dto.ratings = filter.selectedRatings;
    }
    if (filter.selectedPlatforms && filter.selectedPlatforms.length > 0) {
        dto.platforms = filter.selectedPlatforms;
    }
    if (filter.selectedContext && filter.selectedContext.length > 0) {
        dto.context = filter.selectedContext;
    }
    if (filter.selectedContextValues && filter.selectedContextValues.length > 0) {
        dto.contextValues = filter.selectedContextValues;
    }
    if (filter.scoreFrom) {
        dto.score_from = filter.scoreFrom;
    }
    if (filter.scoreTo) {
        dto.score_to = filter.scoreTo;
    }
    if (filter.fulltext) {
        dto.fulltext = filter.fulltext;
    }
    if (filter.aiFilter !== 'NO AI Filter') {
        dto.ai_filter = '';
        if (filter.aiFilter === 'AI Rated') {
            dto.ai_filter = 'AI_RATED';
        } else {
            dto.ai_filter = 'AI_NOT_RATED';
        }
    }
    if (filter.minimalAnswerCount){
        dto.answerCount = filter.minimalAnswerCount;
    }
    if (filter.categories && filter.categories.length > 0) {
        dto.categories = filter.categories;
    } else if (filter.category && filter.category.length > 0) {
        dto.categories = filter.category;
    }
    if (filter.clientNames && filter.clientNames.length > 0){
        dto.clientNames = filter.clientNames;
    }
    if (filter.randomSampleEnabled) {
        dto.randomSample = true;
        if (filter.randomSampleSize) {
            dto.sampleSize = filter.randomSampleSize;
        }
    }
    if (typeof limit === 'number') dto.limit = limit;
    if (typeof offset === 'number') dto.offset = offset;
    if (typeof total === 'boolean') dto.total = total;
    if (lastSessionId) dto.last_session_id = lastSessionId;
    if (lastCreatedAt) {
      dto.last_created_at = typeof lastCreatedAt === 'string' ? lastCreatedAt : new Date(lastCreatedAt).toISOString();
    }
    if (firstSessionId) dto.first_session_id = firstSessionId;
    if (firstCreatedAt) {
      dto.first_created_at = typeof firstCreatedAt === 'string' ? firstCreatedAt : new Date(firstCreatedAt).toISOString();
    }
    return dto
}

const API_PATH = (projectId) =>
  '/api/training/v1/' + encodeURIComponent(projectId) + '/conversations';

const StickyHeadTable = (props) => {
  const classes = useStyles();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [, setLastSessionId] = useState(null);
  const [, setLastCreatedAt] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const [countFetched, setCountFetched] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [displayOffset, setDisplayOffset] = useState(0);
  const filterRef = useRef(null);
  const cursorRef = useRef({
    lastSessionId: null,
    lastCreatedAt: null,
    firstSessionId: null,
    firstCreatedAt: null
  });
  const pageHistoryRef = useRef([]);
  const currentPageIndexRef = useRef(0);
  const { projectId } = props;
  const { t } = useTranslation();

  const resetState = useCallback(() => {
    setData([]);
    setLastSessionId(null);
    setLastCreatedAt(null);
    setTotalCount(null);
    setDataFetched(false);
    setCountFetched(false);
    setHasNextPage(false);
    setHasPreviousPage(false);
    setDisplayOffset(0);
    cursorRef.current = {
      lastSessionId: null,
      lastCreatedAt: null,
      firstSessionId: null,
      firstCreatedAt: null
    };
    pageHistoryRef.current = [];
    currentPageIndexRef.current = 0;
  }, []);

  useEffect(() => {
    if (projectId !== filterRef.current?.projectId || JSON.stringify(props.filter) !== JSON.stringify(filterRef.current?.filter)) {
      filterRef.current = { projectId, filter: props.filter };
      resetState();
    }
  }, [projectId, props.filter, resetState]);

  useEffect(() => {
    if (props.dirtyConversation) {
      setData((prevData) => {
        const index = prevData.findIndex(
          (it) => it.id === props.dirtyConversation.id
        );
        if (index >= 0) {
          const updatedData = [...prevData];
          updatedData[index] = props.dirtyConversation;
          return updatedData;
        }
        return prevData;
      });
    }
  }, [props.dirtyConversation]);

  const getConversation = useCallback((sessionID) => {
    conversationService
      .getConversation(projectId, sessionID)
      .then((response) => {
        const result = response.data;
        if (result) {
          setData([result]);
          setTotalCount(1);
          setCountFetched(true);
          setHasNextPage(false);
          setLastSessionId(null);
          setLastCreatedAt(null);
        } else {
          setData([]);
          setTotalCount(0);
          setCountFetched(true);
          setHasNextPage(false);
        }
        setDataFetched(true);
      })
      .catch(() => {
        setData([]);
        setDataFetched(true);
      });
  }, [projectId]);

  const fetchDataRequest = useCallback((limit, cursor = null) => {
      const isRandomSample = !!props.filter?.randomSampleEnabled;
      const dto = buildJSONFilter(props.filter, {
          limit,
          offset: 0,
          total: false,
          ...(!isRandomSample && cursor?.lastSessionId && {
              lastSessionId: cursor.lastSessionId,
              lastCreatedAt: cursor.lastCreatedAt
          }),
          ...(!isRandomSample && cursor?.firstSessionId && {
              firstSessionId: cursor.firstSessionId,
              firstCreatedAt: cursor.firstCreatedAt
          })
      });
      const path = API_PATH(projectId);
      return CoachingKangaroo.post(path, dto);
  }, [projectId, props.filter]);

  const fetchCountRequest = useCallback(() => {
    if (props.filter?.randomSampleEnabled) {
      const dto = buildJSONFilter(props.filter, { limit: 0, total: true });
      return CoachingKangaroo.post(API_PATH(projectId), dto);
    }
    const dto = buildJSONFilter(props.filter, {
      limit: 0,
      total: true
    });
    return CoachingKangaroo.post(API_PATH(projectId), dto);
  }, [projectId, props.filter]);

  useEffect(() => {
    if (!props.filter) return;

    if (props.filter.inputSessionID) {
      resetState();
      getConversation(props.filter.inputSessionID);
      return;
    }

    setDataFetched(false);
    setCountFetched(false);
    const isRandomSample = !!props.filter.randomSampleEnabled;
    const limit = isRandomSample ? (props.filter.randomSampleSize || 50) : (parseInt(rowsPerPage, 10) || 10);

    fetchDataRequest(limit)
      .then((response) => {
        const conversations = response.data?.conversations;
        const list = Array.isArray(conversations) ? conversations : [];
        setData(list);
        if (!isRandomSample && list.length > 0) {
          const first = list[0];
          const last = list[list.length - 1];
          const firstId = first.id ?? first.session_id ?? null;
          const firstAt = first['started-at'] ?? first.started_at ?? null;
          const lastId = last.id ?? last.session_id ?? null;
          const lastAt = last['started-at'] ?? last.started_at ?? null;
          const pageEntry = {
            list,
            firstId,
            firstAt,
            lastId,
            lastAt,
            hasNext: list.length >= limit,
            displayOffset: 0
          };
          pageHistoryRef.current = [pageEntry];
          currentPageIndexRef.current = 0;
          setLastSessionId(lastId);
          setLastCreatedAt(lastAt);
          cursorRef.current = {
            lastSessionId: lastId,
            lastCreatedAt: lastAt,
            firstSessionId: firstId,
            firstCreatedAt: firstAt
          };
          setHasNextPage(list.length >= limit);
          setHasPreviousPage(false);
          setDisplayOffset(0);
        } else if (!isRandomSample) {
          setHasNextPage(false);
          setHasPreviousPage(false);
        }
      })
      .catch(() => {
        setData([]);
        if (!isRandomSample) {
          setHasNextPage(false);
        }
      })
      .finally(() => setDataFetched(true));

    if (!isRandomSample) {
      fetchCountRequest()
        .then((response) => {
          const count = response.data?.['total-count'];
          setTotalCount(count != null ? count : 0);
        })
        .catch(() => setTotalCount(0))
        .finally(() => setCountFetched(true));
    } else {
      setTotalCount(0);
      setCountFetched(true);
    }

    return () => {};
  }, [props.filter, projectId, rowsPerPage, getConversation, fetchDataRequest, fetchCountRequest, resetState]);

  const handleNext = useCallback(() => {
    if (props.filter?.randomSampleEnabled) return;
    const { lastSessionId: sid, lastCreatedAt: cat } = cursorRef.current;
    if (!sid || !cat || loadingMore || !hasNextPage) return;

    const limit = parseInt(rowsPerPage, 10) || 10;
    const history = pageHistoryRef.current;
    const nextIndex = currentPageIndexRef.current + 1;

    if (nextIndex < history.length) {
      const page = history[nextIndex];
      setData(page.list);
      setLastSessionId(page.lastId);
      setLastCreatedAt(page.lastAt);
      cursorRef.current = {
        lastSessionId: page.lastId,
        lastCreatedAt: page.lastAt,
        firstSessionId: page.firstId,
        firstCreatedAt: page.firstAt
      };
      setHasNextPage(page.hasNext);
      setHasPreviousPage(true);
      setDisplayOffset(page.displayOffset);
      currentPageIndexRef.current = nextIndex;
      return;
    }

    setLoadingMore(true);
    fetchDataRequest(limit, { lastSessionId: sid, lastCreatedAt: cat })
      .then((response) => {
        const conversations = response.data?.conversations ?? (Array.isArray(response.data) ? response.data : []);
        const list = Array.isArray(conversations) ? conversations : [];
        if (list.length === 0) {
          setHasNextPage(false);
          return;
        }
        const first = list[0];
        const last = list[list.length - 1];
        const firstId = first.id ?? first.session_id ?? null;
        const firstAt = first['started-at'] ?? first.started_at ?? null;
        const lastId = last.id ?? last.session_id ?? null;
        const lastAt = last['started-at'] ?? last.started_at ?? null;
        const displayOffset = (history.length > 0 ? history[currentPageIndexRef.current].displayOffset + limit : limit);
        const pageEntry = {
          list,
          firstId,
          firstAt,
          lastId,
          lastAt,
          hasNext: list.length >= limit,
          displayOffset
        };
        pageHistoryRef.current = [...history, pageEntry];
        currentPageIndexRef.current = pageHistoryRef.current.length - 1;
        setData(list);
        setLastSessionId(lastId);
        setLastCreatedAt(lastAt);
        cursorRef.current = {
          lastSessionId: lastId,
          lastCreatedAt: lastAt,
          firstSessionId: firstId,
          firstCreatedAt: firstAt
        };
        setHasNextPage(list.length >= limit);
        setHasPreviousPage(true);
        setDisplayOffset(displayOffset);
      })
      .catch(() => setHasNextPage(false))
      .finally(() => setLoadingMore(false));
  }, [loadingMore, hasNextPage, rowsPerPage, fetchDataRequest, props.filter?.randomSampleEnabled]);

  const handlePrevious = useCallback(() => {
    if (props.filter?.randomSampleEnabled) return;
    if (loadingMore || !hasPreviousPage) return;

    const prevIndex = currentPageIndexRef.current - 1;
    if (prevIndex < 0) return;

    const page = pageHistoryRef.current[prevIndex];
    if (!page) return;

    setData(page.list);
    setLastSessionId(page.lastId);
    setLastCreatedAt(page.lastAt);
    cursorRef.current = {
      lastSessionId: page.lastId,
      lastCreatedAt: page.lastAt,
      firstSessionId: page.firstId,
      firstCreatedAt: page.firstAt
    };
    setHasNextPage(true);
    setHasPreviousPage(prevIndex > 0);
    setDisplayOffset(page.displayOffset);
    currentPageIndexRef.current = prevIndex;
  }, [loadingMore, hasPreviousPage, props.filter?.randomSampleEnabled]);

  const handleChangeRowsPerPage = (event) => {
    if (props.filter?.randomSampleEnabled) return;
    const newRowsPerPage = +event.target.value;
    setRowsPerPage(newRowsPerPage);
    setData([]);
    setLastSessionId(null);
    setLastCreatedAt(null);
    setTotalCount(null);
    setDataFetched(false);
    setCountFetched(false);
    setHasNextPage(false);
    setHasPreviousPage(false);
    setDisplayOffset(0);
    cursorRef.current = {
      lastSessionId: null,
      lastCreatedAt: null,
      firstSessionId: null,
      firstCreatedAt: null
    };
    pageHistoryRef.current = [];
    currentPageIndexRef.current = 0;
  };

  const formatTotalCount = () => {
    if (!countFetched || totalCount === null) return t('Counting');
    if (totalCount < 0) return `${Math.abs(totalCount)}+`;
    return String(totalCount);
  };

  const displayedFrom = props.filter?.randomSampleEnabled ? (data.length > 0 ? 1 : 0) : displayOffset + 1;
  const displayedTo = props.filter?.randomSampleEnabled ? data.length : displayOffset + data.length;

  const paginationBar = (
    <Box className={classes.paginationBar}>
      <Box className={classes.paginationInfo}>
        {!props.filter?.randomSampleEnabled && (
          <FormControl size="small" variant="standard" className={classes.selectBorderless} style={{ minWidth: 48 }}>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              displayEmpty
              input={<Input disableUnderline />}
              style={{
                fontSize: '0.875rem',
                border: 'none',
                background: 'transparent'
              }}
              MenuProps={{
                MenuListProps: { style: { padding: '4px 0' } }
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        )}
        <span>
          {props.filter?.randomSampleEnabled
            ? `${t('Displayed')} ${data.length} ${t('random_conversations')}`
            : <>
                {data.length === 0
                  ? `${t('Displayed')} 0`
                  : `${t('Displayed')} ${displayedFrom}-${displayedTo}`}
                {countFetched ? ` ${t('of')} ${formatTotalCount()}` : ` (${formatTotalCount()}...)`}
              </>
          }
        </span>
      </Box>
      {!props.filter?.randomSampleEnabled && (
        <Box className={classes.paginationActions}>
          <Button
            size="small"
            onClick={handlePrevious}
            disabled={!hasPreviousPage || loadingMore}
          >
            {t('Previous')}
          </Button>
          <Button
            size="small"
            onClick={handleNext}
            disabled={!hasNextPage || loadingMore}
          >
            {t('Next')}
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Paper className={classes.root}>
      {paginationBar}
      <TableContainer className={classes.container}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth, maxWidth: column.maxWidth, width: column.maxWidth }}
                >
                  {t(column.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {(() => {
            if (!dataFetched) {
              return <MaterialSpinner />;
            } else if (data.length === 0 && (countFetched ? totalCount === 0 : true)) {
              const row = { text: t('no_conversations') };
              return (
                <TableBody>
                  <MaterialTableRow key="-1" row={row} columns={columns} />
                </TableBody>
              );
            } else if (data && data.length > 0) {
              return (
                <TableBody>
                  {data.map((row, index) => (
                    <MaterialTableRow
                      key={row.id}
                      row={row}
                      columns={columns}
                      onClick={() =>
                        props.onClick(
                          row,
                          index,
                          data,
                          index < data.length - 1 || hasNextPage,
                          Math.floor(displayOffset / rowsPerPage),
                          rowsPerPage
                        )
                      }
                    />
                  ))}
                </TableBody>
              );
            } else {
              const row = { text: t('fetching_data_error') };
              return (
                <TableBody>
                  <MaterialTableRow key="-1" row={row} columns={columns} />
                </TableBody>
              );
            }
          })()}
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default StickyHeadTable;
